import {
    DistributionMethod,
    PercentGrowthBasePeriod,
    VersionType,
    UserRole, RevenueType
} from "../../../../../__generated__/generated_types";
import { MONTHS } from "../../../../../constants/Months";
import { Property } from "../../../../../contexts/properties/logic";
import { AcquisitionPropertyVersion } from "../../../../acquisition-property/AcquisitionProperties";

export const NON_EXCLUSIVE_REVENUE_TYPES: RevenueType[] = [
    RevenueType.VacancyAdmin,
    RevenueType.VacancyDown,
    RevenueType.VacancyModel,
];

export interface BaseToGrow {
  value: PercentGrowthBasePeriod;
  label: string;
}

export const stringToFixedFloat = (str: string, decimals: number, validatePercent: boolean): number => {
    let floatValue = parseFloat(parseFloat(str).toFixed(decimals));

    if (validatePercent) {
        floatValue = floatValue >= 0 && floatValue <= 100 ? floatValue : 0;
    }

    return floatValue;
};

export const distributionOptions = [
    { name: DistributionMethod.Flat, value: "even distribution" },
    { name: DistributionMethod.Seasonal, value: "seasonal distribution" },
];

export type ModelingMethodName = "account" | "operational" | "revenue" | "worksheet" | "renovations" | "payroll" | "percentGrowth" | "monthlyAverage" | "yearlyAverage" | "annualTargetValue" | "customDriver" | "operationalMonthlyAverage";

export interface AccountViewFxBarUserPermissionsMap {
    [UserRole.Admin]: ModelingMethodName[];
    [UserRole.Manager]: ModelingMethodName[];
}

export const accountViewFxBarUserPermissionsMap: AccountViewFxBarUserPermissionsMap = {
    [UserRole.Admin]: ["account", "operational", "revenue", "worksheet", "renovations", "payroll", "percentGrowth", "monthlyAverage", "yearlyAverage", "annualTargetValue", "customDriver"],
    [UserRole.Manager]: ["worksheet", "percentGrowth", "monthlyAverage", "yearlyAverage", "annualTargetValue"],
};

export const canAddDriversToEmptyFxBar: boolean = (() => {
    let canAdd = true;

    /**
     * If all user roles are allowed to edit at least 1 modeling method, then canAddDriversToEmptyFxBar = true
    */
    Object.values(accountViewFxBarUserPermissionsMap).forEach((editableModelingMethods) => {
        if (editableModelingMethods.length === 0) {
            canAdd = false;
        }
    });

    return canAdd;
})();

// Ref - https://www.notion.so/vizibly/Growth-Drivers-30142e24d53b4e3ab864088359bcab73?pvs=4#d6b44e4601e04dc883eea7591e2da804
export const getBaseToGrowOptions = (
  versionType: VersionType.Budget | VersionType.Reforecast,
  reforecastYear: number
): BaseToGrow[] => {
  if (versionType === VersionType.Budget) {
    return [
      {
        value: PercentGrowthBasePeriod.SameMonthLastYear,
        label: "Same month last year",
      },
      {
        value: PercentGrowthBasePeriod.BudgetSameMonthCurrentYear,
        label: `Same month ${reforecastYear} budget amount`,
      },
      {
        value: PercentGrowthBasePeriod.LastYearBudget,
        label: `${reforecastYear} Budget Total`,
      },
      {
        value: PercentGrowthBasePeriod.LastYearActuals,
        label: `${reforecastYear} Reforecast Total`,
      },
      {
        value: PercentGrowthBasePeriod.TwoYearsAgoActuals,
        label: `${reforecastYear - 1} Actuals Total`,
      },
    ];
  }

  return [
    {
      value: PercentGrowthBasePeriod.SameMonthLastYear,
      label: "Same month last year",
    },
    {
      value: PercentGrowthBasePeriod.BudgetSameMonthCurrentYear,
      label: `Same month ${reforecastYear} budget amount`,
    },
    {
      value: PercentGrowthBasePeriod.CurrentYearBudget,
      label: `${reforecastYear} Budget Total`,
    },
    {
      value: PercentGrowthBasePeriod.LastYearActuals,
      label: `${reforecastYear - 1} Actuals Total`,
    },
    {
      value: PercentGrowthBasePeriod.TwoYearsAgoActuals,
      label: `${reforecastYear - 2} Actuals Total`,
    },
  ];
};

/**
Acquisition property helper to map a date from the display value to the correct database value
@param date string date in format YYYY-MM-DD
@param property property object
@returns a date in the string format YYYY-MM-DD, or empty string if there was a problem
*/
export function transposeAcquisitionDateDisplayToDatabase(date: string, property: Property): string {
    if (!date) {
        return "";
    }

    const [year, month, day] = date.split("-");

    if (!property.acquisitionBudgetYear ||
        property.acquisitionBudgetStartMonth == null ||
        !year ||
        month == null ||
        day == null
    ) {
        return "";
    }

    let transposedYear = property.reforecastYear - 1;
    const intMonth = parseInt(month) - 1;
    let transposedMonth: string | number = (intMonth - property.acquisitionBudgetStartMonth + 12) % 12;

    const acquisitionVersion = getAcquisitionPropertyVersionForDisplayDate(parseInt(month) - 1, parseInt(year), property);
    if (acquisitionVersion == AcquisitionPropertyVersion.Year1) {
        transposedYear += 1;
    }

    transposedMonth = String(transposedMonth + 1).padStart(2, '0');

    return `${transposedYear}-${transposedMonth}-${day}`;
}

/**
Acquisition property helper to map a date from the database value to the correct display value
@param date string date in format YYYY-MM-DD
@param property property object
@returns a date in the string format YYYY-MM-DD, or empty string if there was a problem
*/
export function transposeAcquisitionDateDatabaseToDisplay(date: string, property: Property): string {
    if (!date) {
        return "";
    }

    const [year, month, day] = date.split("-");

    if (!property.acquisitionBudgetYear ||
        property.acquisitionBudgetStartMonth == null ||
        !year ||
        month == null ||
        day == null
    ) {
        return "";
    }

    const intMonth = parseInt(month) - 1;
    const intYear = parseInt(year);
    let transposedMonth: string | number = (property.acquisitionBudgetStartMonth + intMonth + 12) % 12;
    let transposedYear = transposedMonth < property.acquisitionBudgetStartMonth ? property.acquisitionBudgetYear : property.acquisitionBudgetYear - 1;
    transposedMonth = String(transposedMonth + 1).padStart(2, '0');

    if (intYear == property.reforecastYear) {
        transposedYear += 1;
    }

    return `${transposedYear}-${transposedMonth}-${day}`;
}

/**
Acquisition property helper to take a display date and tell you if it's T12 or Year1
@param month 0 indexed month
@param year number
@param property property object
@returns a date in the string format YYYY-MM-DD, or empty string if there was a problem
*/
export function getAcquisitionPropertyVersionForDisplayDate(month: number, year: number, property: Property): AcquisitionPropertyVersion | null {
    if (property.acquisitionBudgetYear == null ||
        property.acquisitionBudgetStartMonth == null
    ) {
        return null;
    }

    let version = AcquisitionPropertyVersion.T12;

    if (
        (month >= property.acquisitionBudgetStartMonth && year == property.acquisitionBudgetYear) ||
        (month < property.acquisitionBudgetStartMonth && year > property.acquisitionBudgetYear)
    ) {
        version = AcquisitionPropertyVersion.Year1;
    }

    return version;
}

export function acquisitionPropertyDateDisplayString(date: string, property: Property): string {
    if (!date || date == "") {
        return "";
    }

    if (property.isAcquisitionProperty) {
        const [year, month] = date.split("-");

        if (month == null || year == null) {
            return "";
        }

        const intYear = parseInt(year);
        const intMonth = parseInt(month) - 1;
        const displayYear = getAcquisitionPropertyVersionForDisplayDate(intMonth, intYear, property);
        return `${MONTHS[intMonth]} ${displayYear}`;
    }

    return date;
}

export function getDateForLastDayInMonth(dateString: string): string | null {
    if (!dateString) {
        return null;
    }

    const date = new Date(dateString);
    const dateAtEndOfMonth = new Date(date.getUTCFullYear(), date.getUTCMonth() + 1, 0);

    return `${dateAtEndOfMonth.getFullYear()}-${dateAtEndOfMonth.getMonth() + 1}-${dateAtEndOfMonth.getDate()}`;
}
